<template>
    <div class="advisories-tab-container" :aria-busy="pending">
        <p class="sr-only" role="status">{{ advisoriesStatus }}</p>
        <SharedSpinner v-if="pending" label="Loading advisories..." />
        <div v-else-if="advisories.length === 0" class="no-advisories">
            <NoAdvisories />
        </div>
        <div v-else class="main">
            <AdvisoriesToolTip class="pb-10" />
            <div v-if="!showSpecificIncident" class="card-div">
                <AdvisoryCard 
                    v-for="advisory in advisories" 
                    :key="advisory.id"
                    :advisory="advisory"
                    @handleShowSpecificIncident="handleShowSpecificIncident" 
                />
            </div>
            <SpecificIncident v-else :advisory="selectedAdvisory" @handleShowSpecificIncident="handleShowSpecificIncident" />
        </div>
    </div>
</template>

<script setup lang="ts">
import AdvisoryCard from '~/components/main/person/companies/CompanyView/CompanyTabs/AdvisoriesTab/AdvisoryCard.vue';
import NoAdvisories from '~/components/main/person/companies/CompanyView/CompanyTabs/AdvisoriesTab/NoAdvisories.vue';
import SpecificIncident from '~/components/main/person/companies/CompanyView/CompanyTabs/AdvisoriesTab/SpecificIncident.vue';
import { companiesService } from '~/services/companies-service';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import type { CompanyAdvisory } from '~/types/person/companies';
import AdvisoriesToolTip from '~/components/main/person/companies/CompanyView/CompanyTabs/AdvisoriesTab/AdvisoriesToolTip.vue';

const exposureStore = useExposureSidebarStore();
const { company } = storeToRefs(exposureStore);

const showSpecificIncident = ref(false);
const advisories = ref<CompanyAdvisory[]>([]);
const pending = ref(false);
const currentAdvisory = ref<CompanyAdvisory | null>(null);
const loadFailed = ref(false);
const lastFetchedCompanyId = ref<number | null>(null);

// The detail view's state is announced from here because this region outlives the
// list/detail swap; a region mounted by that swap holds its text from birth and never announces.
const advisoriesStatus = computed((): string => {
    if (pending.value) return 'Loading advisories';
    // A failed fetch empties the list, and nothing else on this tab reports it — only a
    // console line — so "none found" would be the only thing the member ever hears.
    if (loadFailed.value) return 'Advisories could not be loaded';
    if (!advisories.value.length) return 'No advisories found';
    if (showSpecificIncident.value) return currentAdvisory.value ? 'Advisory details loaded' : 'Loading advisory';
    return `${advisories.value.length} ${advisories.value.length === 1 ? 'advisory' : 'advisories'}`;
});

const selectedAdvisory = computed((): CompanyAdvisory | null => {
    if (currentAdvisory.value) {
        return currentAdvisory.value;
    }
    return null;
});

const fetchAdvisories = async () => {
    if (!company.value?.id) {
        advisories.value = [];
        pending.value = false;
        return;
    }

    // Only fetch if we haven't already fetched for this company
    if (lastFetchedCompanyId.value === company.value.id) {
        return;
    }

    loadFailed.value = false;

    try {
        pending.value = true;
        lastFetchedCompanyId.value = company.value.id;
        advisories.value = await companiesService.getCompanyAdvisories(company.value.id);
    } catch (error) {
        console.error('Failed to fetch advisories:', error);
        loadFailed.value = true;
        advisories.value = [];
    } finally {
        pending.value = false;
    }
};

const handleShowSpecificIncident = (advisoryId?: string) => {
    if (showSpecificIncident.value) {
        // Going back to list view
        showSpecificIncident.value = false;
        currentAdvisory.value = null;
    } else {
        // Going to specific incident view
        showSpecificIncident.value = true;
        
        if (advisoryId) {
            // Find the advisory and set it directly
            const advisory = advisories.value.find(a => a.id === advisoryId);
            if (advisory) {
                currentAdvisory.value = advisory;
            }
        }
    }
};

watch(() => company.value?.id, (newId) => {
    if (newId && newId !== lastFetchedCompanyId.value) {
        fetchAdvisories();
    }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.advisories-tab-container {
    @apply flex flex-col;
}

.main {
    @apply pt-6 px-6;
}

.card-div {
    @apply flex flex-col gap-6;
}

.no-advisories {
    @apply w-full;
}
</style>

